import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../apiClient'; // Import apiClient for direct backend communication
import './AssessmentTemplateManager.css';
import { FaEdit, FaCopy, FaTrash, FaPlusCircle, FaEye, FaMagic } from 'react-icons/fa';
import CreateEditTemplateModal from '../components/assessment/CreateEditTemplateModal';
import TakeAssessmentModal from '../components/assessment/TakeAssessmentModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

function QuestionManagerModal({ template, isOpen, onClose, onQuestionsUpdated }) {
  const [questions, setQuestions] = useState([]);
  const [qLoading, setQLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ text: '', type: 'scale', options: ['',''], required: false, helper_text: '', placeholder: '' });
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState(null);

  const fetchQuestions = useCallback(async () => {
    if (!template || !template.id) return;
    try {
      const res = await apiClient.get(`/assessment-templates/${template.id}/questions`);
      setQuestions(res.data);
    } catch (err) {
      setError('Failed to load questions.');
    } finally {
      setQLoading(false);
    }
  }, [template]);

  useEffect(() => {
    if (!isOpen) return;
    setQLoading(true);
    setError(null);
    fetchQuestions();
  }, [isOpen, fetchQuestions]);

  const resetForm = () => {
    setForm({ text: '', type: 'scale', options: ['',''], required: false, helper_text: '', placeholder: '' });
    setEditingId(null);
    setFormError(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    if (name === "type" && value === "likert") {
      setForm(f => ({ ...f, type: value, options: [...LIKERT_OPTIONS] }));
    } else if (name === "type" && value === "comfort_scale") {
      setForm(f => ({ ...f, type: value, options: [...COMFORT_OPTIONS] }));
    } else if (name === "type" && value === "confidence_scale") {
      setForm(f => ({ ...f, type: value, options: [...CONFIDENCE_OPTIONS] }));
    } else if (name === "type" && value === "frequency_scale") {
      setForm(f => ({ ...f, type: value, options: [...FREQUENCY_OPTIONS] }));
    } else if (name === "type" && value === "yes_no") {
      setForm(f => ({ ...f, type: value, options: ["Yes", "No"] }));
    } else if (name === "type" && value === "ranking") {
      setForm(f => ({ ...f, type: value, options: ["Option 1", "Option 2", "Option 3"] }));
    } else if (name === "type" && ["multi_select", "single_select"].includes(value)) {
      setForm(f => ({ ...f, type: value, options: ["", ""] }));
    } else if (name === "type") {
      setForm(f => ({ ...f, type: value, options: [] }));
    } else {
      setForm(f => ({ ...f, [name]: inputType === 'checkbox' ? checked : value }));
    }
  };

  const handleOptionChange = (idx, value) => {
    setForm(f => {
      const opts = [...f.options];
      opts[idx] = value;
      return { ...f, options: opts };
    });
  };

  const addOption = () => setForm(f => ({ ...f, options: [...f.options, ''] }));
  const removeOption = (idx) => setForm(f => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));

  const validateForm = () => {
    if (!form.text.trim()) return 'Question text is required.';
    if (["multi_select", "single_select"].includes(form.type) && (!form.options || form.options.length < 2 || form.options.some(opt => !opt.trim()))) return 'Select questions need at least 2 options, all filled.';
    if (form.type === "ranking" && (!form.options || form.options.length < 2 || form.options.some(opt => !opt.trim()))) return 'Ranking questions need at least 2 items, all filled.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) return setFormError(err);
    setFormError(null);
    try {
      const payload = { ...form, label: form.text, type: form.type };
      console.log('Submitting question:', payload);
      console.log('Template info:', template);
      console.log('Current user info:', {
        userRole: JSON.parse(localStorage.getItem('ezocmUser'))?.role,
        userId: JSON.parse(localStorage.getItem('ezocmUser'))?.id,
        userIdType: typeof JSON.parse(localStorage.getItem('ezocmUser'))?.id
      });
      
      if (editingId) {
        console.log('Updating question:', editingId);
        await apiClient.put(`/assessment-templates/questions/${editingId}`, payload);
      } else {
        console.log('Adding new question to template:', template.id);
        const response = await apiClient.post(`/assessment-templates/${template.id}/questions`, payload);
        console.log('Question add response:', response);
      }
      resetForm();
      fetchQuestions();
      if (onQuestionsUpdated) onQuestionsUpdated();
    } catch (err) {
      console.error('Error saving question:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Full error object:', err);
      setFormError(`Failed to save question: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleEdit = (q) => {
    setForm({
      text: q.text,
      type: q.type,
      options: q.options || ['',''],
      required: q.required,
      helper_text: q.helper_text || '',
      placeholder: q.placeholder || ''
    });
    setEditingId(q.id);
    setFormError(null);
  };

  const [deleteQuestionModal, setDeleteQuestionModal] = useState({ isOpen: false, questionId: null, questionText: '' });

  const handleDelete = (question) => {
    setDeleteQuestionModal({
      isOpen: true,
      questionId: question.id,
      questionText: question.text
    });
  };

  const confirmDeleteQuestion = async () => {
    try {
      console.log('Deleting question:', deleteQuestionModal.questionId);
      await apiClient.delete(`/assessment-templates/questions/${deleteQuestionModal.questionId}`);
      fetchQuestions();
      if (onQuestionsUpdated) onQuestionsUpdated();
      setDeleteQuestionModal({ isOpen: false, questionId: null, questionText: '' });
    } catch (err) {
      console.error('Error deleting question:', err);
      console.error('Delete error response:', err.response?.data);
      setError(`Failed to delete question: ${err.response?.data?.error || err.message}`);
    }
  };

  return isOpen ? (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 800 }}>
        <div className="modal-header">
          <h2>Manage Questions for: {template.name}</h2>
          <button onClick={onClose} className="close-button">&times;</button>
        </div>
        <div className="modal-body">
          {qLoading ? <p>Loading questions...</p> : error ? <p style={{ color: 'red' }}>{error}</p> : (
            <>
              {questions.length === 0 ? <p>No questions yet.</p> : (
                <ol style={{ paddingLeft: 20 }}>
                  {questions.map(q => (
                    <li key={q.id} style={{
                      marginBottom: 10,
                      background: editingId === q.id ? '#e3f2fd' : '#f9f9f9',
                      borderRadius: 6,
                      padding: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: editingId === q.id ? '2px solid #1976d2' : '1px solid #eee'
                    }}>
                      {editingId === q.id ? (
                        <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <input
                            type="text"
                            name="text"
                            value={form.text}
                            onChange={handleFormChange}
                            required
                            style={{ marginBottom: 6 }}
                          />
                          <select name="type" value={form.type} onChange={handleFormChange} style={{ marginBottom: 6 }}>
                            {typeOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          {form.type === 'likert' && (
                            <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                              {LIKERT_OPTIONS.map((opt, i) => (
                                <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input type="radio" disabled /> {opt}
                                </label>
                              ))}
                            </div>
                          )}
                          {form.type === 'comfort_scale' && (
                            <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                              {COMFORT_OPTIONS.map((opt, i) => (
                                <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input type="radio" disabled /> {opt}
                                </label>
                              ))}
                            </div>
                          )}
                          {form.type === 'confidence_scale' && (
                            <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                              {CONFIDENCE_OPTIONS.map((opt, i) => (
                                <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input type="radio" disabled /> {opt}
                                </label>
                              ))}
                            </div>
                          )}
                          {form.type === 'frequency_scale' && (
                            <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                              {FREQUENCY_OPTIONS.map((opt, i) => (
                                <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input type="radio" disabled /> {opt}
                                </label>
                              ))}
                            </div>
                          )}
                          {form.type === 'yes_no' && (
                            <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <input type="radio" disabled /> Yes
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <input type="radio" disabled /> No
                              </label>
                            </div>
                          )}
                          {form.type === 'ranking' && (
                            <div className="form-group">
                              <label>Items to Rank *</label>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                                {form.options.map((opt, idx) => (
                                  <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ fontSize: '1.2rem' }}>☰</span>
                                    <input
                                      type="text"
                                      value={opt}
                                      onChange={e => handleOptionChange(idx, e.target.value)}
                                      required
                                      placeholder={`Item ${idx + 1}`}
                                      style={{ width: 120 }}
                                    />
                                    {form.options.length > 2 && <button type="button" onClick={() => removeOption(idx)} style={{ color: 'red' }}>x</button>}
                                  </span>
                                ))}
                                <button type="button" onClick={addOption}>Add Item</button>
                              </div>
                            </div>
                          )}
                          {["multi_select", "single_select"].includes(form.type) && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                              {form.options.map((opt, idx) => (
                                <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input
                                    type="text"
                                    value={opt}
                                    onChange={e => handleOptionChange(idx, e.target.value)}
                                    required
                                    style={{ width: 100 }}
                                  />
                                  {form.options.length > 2 && <button type="button" onClick={() => removeOption(idx)} style={{ color: 'red' }}>x</button>}
                                </span>
                              ))}
                              <button type="button" onClick={addOption}>Add Option</button>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 10 }}>
                            <button type="submit" title="Save" style={{ background: 'none', border: 'none', color: '#1976d2', fontSize: 20, cursor: 'pointer' }}>✔️</button>
                            <button type="button" title="Cancel" onClick={resetForm} style={{ background: 'none', border: 'none', color: '#c00', fontSize: 20, cursor: 'pointer' }}>❌</button>
                          </div>
                          {formError && <div style={{ color: 'red', marginTop: 4 }}>{formError}</div>}
                        </form>
                      ) : (
                        <>
                          <div>
                            <b>{q.text}</b> {q.type && <span style={{ color: '#888', fontSize: '0.95rem' }}>({typeOptions.find(t => t.value === q.type)?.label || q.type})</span>}
                            {q.type === 'likert' && (
                              <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                                {LIKERT_OPTIONS.map((opt, i) => (
                                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="radio" disabled /> {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'comfort_scale' && (
                              <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                                {COMFORT_OPTIONS.map((opt, i) => (
                                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="radio" disabled /> {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'confidence_scale' && (
                              <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                                {CONFIDENCE_OPTIONS.map((opt, i) => (
                                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="radio" disabled /> {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'frequency_scale' && (
                              <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                                {FREQUENCY_OPTIONS.map((opt, i) => (
                                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="radio" disabled /> {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'scale' && (
                              <div style={{ display: 'flex', gap: '2rem', color: '#888', margin: '0.5rem 0' }}>
                                {[1,2,3,4,5].map(n => (
                                  <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="radio" disabled /> {n}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'multi_select' && q.options && Array.isArray(q.options) && (
                              <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                                {q.options.map((opt, i) => (
                                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="checkbox" disabled /> {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'single_select' && q.options && Array.isArray(q.options) && (
                              <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                                {q.options.map((opt, i) => (
                                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input type="radio" disabled /> {opt}
                                  </label>
                                ))}
                              </div>
                            )}
                            {q.type === 'yes_no' && (
                              <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input type="radio" disabled /> Yes
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input type="radio" disabled /> No
                                </label>
                              </div>
                            )}
                            {q.type === 'ranking' && q.options && Array.isArray(q.options) && (
                              <div style={{ margin: '0.5rem 0' }}>
                                <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                                  Drag items to rank them:
                                </div>
                                {q.options.map((opt, i) => (
                                  <div key={i} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 8, 
                                    padding: '4px 8px',
                                    margin: '2px 0',
                                    background: '#f5f5f5',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'move'
                                  }}>
                                    <span style={{ fontSize: '1.2rem' }}>☰</span>
                                    <span style={{ fontWeight: 'bold', minWidth: '20px' }}>{i + 1}.</span>
                                    <span>{opt}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <span style={{ display: 'flex', gap: 10 }}>
                            <FaEdit className="icon-action" title="Edit" onClick={() => handleEdit(q)} />
                            <FaTrash className="icon-action" title="Delete" onClick={() => handleDelete(q)} />
                          </span>
                        </>
                      )}
                    </li>
                  ))}
                </ol>
              )}
              {editingId === null && (
                <form onSubmit={handleSubmit} style={{ marginTop: 10 }}>
                  <h4>Add New Question</h4>
                  <div className="form-group">
                    <label>Question Text *</label>
                    <input type="text" name="text" value={form.text} onChange={handleFormChange} required />
                  </div>
                  <div className="form-group">
                    <label>Type *</label>
                    <select name="type" value={form.type} onChange={handleFormChange}>
                      {typeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  {form.type === 'likert' && (
                    <div className="form-group">
                      <label>Likert Options</label>
                      <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                        {LIKERT_OPTIONS.map((opt, idx) => (
                          <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="radio" disabled /> {opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.type === 'comfort_scale' && (
                    <div className="form-group">
                      <label>Comfort Scale Options</label>
                      <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                        {COMFORT_OPTIONS.map((opt, idx) => (
                          <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="radio" disabled /> {opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.type === 'confidence_scale' && (
                    <div className="form-group">
                      <label>Confidence Scale Options</label>
                      <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                        {CONFIDENCE_OPTIONS.map((opt, idx) => (
                          <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="radio" disabled /> {opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.type === 'frequency_scale' && (
                    <div className="form-group">
                      <label>Frequency Scale Options</label>
                      <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                        {FREQUENCY_OPTIONS.map((opt, idx) => (
                          <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="radio" disabled /> {opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.type === 'yes_no' && (
                    <div className="form-group">
                      <label>Response Options</label>
                      <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input type="radio" disabled /> Yes
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input type="radio" disabled /> No
                        </label>
                      </div>
                    </div>
                  )}
                  {form.type === 'ranking' && (
                    <div className="form-group">
                      <label>Items to Rank *</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        {form.options.map((opt, idx) => (
                          <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: '1.2rem' }}>☰</span>
                            <input
                              type="text"
                              value={opt}
                              onChange={e => handleOptionChange(idx, e.target.value)}
                              required
                              placeholder={`Item ${idx + 1}`}
                              style={{ width: 120 }}
                            />
                            {form.options.length > 2 && <button type="button" onClick={() => removeOption(idx)} style={{ color: 'red' }}>x</button>}
                          </span>
                        ))}
                        <button type="button" onClick={addOption}>Add Item</button>
                      </div>
                    </div>
                  )}
                  {["multi_select", "single_select"].includes(form.type) && (
                    <div className="form-group">
                      <label>Options *</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        {form.options.map((opt, idx) => (
                          <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              type="text"
                              value={opt}
                              onChange={e => handleOptionChange(idx, e.target.value)}
                              required
                              style={{ width: 100 }}
                            />
                            {form.options.length > 2 && <button type="button" onClick={() => removeOption(idx)} style={{ color: 'red' }}>x</button>}
                          </span>
                        ))}
                        <button type="button" onClick={addOption}>Add Option</button>
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Helper Text</label>
                    <input type="text" name="helper_text" value={form.helper_text} onChange={handleFormChange} />
                  </div>
                  <div className="form-group">
                    <label>Placeholder</label>
                    <input type="text" name="placeholder" value={form.placeholder} onChange={handleFormChange} />
                  </div>
                  <div className="form-group">
                    <label><input type="checkbox" name="required" checked={form.required} onChange={handleFormChange} /> Required</label>
                  </div>
                  {formError && <div style={{ color: 'red', marginBottom: 8 }}>{formError}</div>}
                  <div className="form-actions">
                    <button type="submit" className="save-btn">Add Question</button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
        
        <DeleteConfirmationModal
          isOpen={deleteQuestionModal.isOpen}
          onClose={() => setDeleteQuestionModal({ isOpen: false, questionId: null, questionText: '' })}
          onConfirm={confirmDeleteQuestion}
          title="Confirm Deletion"
          message={`Are you sure you want to delete the question "${deleteQuestionModal.questionText}"?`}
          confirmButtonText="Delete Question"
        />
      </div>
    </div>
  ) : null;
}

const LIKERT_OPTIONS = [
  "Strongly Disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly Agree"
];

const COMFORT_OPTIONS = [
  "Very Uncomfortable",
  "Uncomfortable", 
  "Neutral",
  "Comfortable",
  "Very Comfortable"
];

const CONFIDENCE_OPTIONS = [
  "Not Confident",
  "Slightly Confident",
  "Moderately Confident", 
  "Confident",
  "Very Confident"
];

const FREQUENCY_OPTIONS = [
  "Never",
  "Rarely",
  "Sometimes",
  "Often", 
  "Always"
];

const typeOptions = [
  { value: "scale", label: "Scale 1-5" },
  { value: "likert", label: "Likert (Strongly Disagree to Strongly Agree)" },
  { value: "comfort_scale", label: "Comfort Scale (Very Uncomfortable to Very Comfortable)" },
  { value: "confidence_scale", label: "Confidence Scale (Not Confident to Very Confident)" },
  { value: "frequency_scale", label: "Frequency Scale (Never to Always)" },
  { value: "yes_no", label: "Yes/No" },
  { value: "multi_select", label: "Option (Multi-select)" },
  { value: "single_select", label: "Option (Single-select)" },
  { value: "ranking", label: "Ranking (Drag & Drop)" },
  { value: "long_text", label: "Long Text" },
  { value: "short_text", label: "Short Text" }
];

function PreviewAssessmentModal({ isOpen, onClose, template, questions, isLoading }) {
  if (!isOpen || !template) return null;
  const assessment = {
    title: template.name,
    description: template.description,
    questions: questions,
    template: template,
  };
  if (isLoading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" style={{textAlign: 'center', padding: '2rem'}}>
          <p>Loading preview...</p>
        </div>
      </div>
    );
  }
  return (
    <TakeAssessmentModal
      isOpen={isOpen}
      onClose={onClose}
      assessment={assessment}
      mode="preview"
      userResult={{ answers: {} }}
    />
  );
}

function AssessmentTemplateManager() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [questionModalTemplate, setQuestionModalTemplate] = useState(null);
  const [deleteTemplateModal, setDeleteTemplateModal] = useState({ isOpen: false, templateId: null, templateName: '' });
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setError(null);
    try {
      const response = await apiClient.get('/assessment-templates');
      setTemplates(Array.isArray(response.data.templates) ? response.data.templates : []);
    } catch (err) {
      console.error("Failed to fetch assessment templates:", err);
      setError("Failed to load templates. Please try again later.");
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    // Get user role and ID from localStorage
    try {
      const userData = JSON.parse(localStorage.getItem('ezocmUser'));
      console.log('Raw localStorage ezocmUser:', localStorage.getItem('ezocmUser'));
      console.log('Parsed userData:', userData);
      console.log('User role:', userData?.role);
      console.log('User ID:', userData?.id);
      console.log('User ID type:', typeof userData?.id);
      setUserRole(userData?.role);
      // Ensure currentUserId is stored as an integer
      setCurrentUserId(userData?.id ? parseInt(userData.id) : null);
    } catch (e) {
      console.error('Error loading user data:', e);
    }
  }, [fetchTemplates]);

  // Helper function to check if user can edit/delete a template
  const canModifyTemplate = (template) => {
    console.log('canModifyTemplate check:', {
      userRole,
      currentUserId,
      currentUserIdType: typeof currentUserId,
      templateId: template.id,
      templateCreatedBy: template.created_by_employee_id,
      templateCreatedByType: typeof template.created_by_employee_id,
      templateCreatedByName: template.created_by_name,
      canModify: userRole === 'Admin' || (userRole === 'Change Manager' && template.created_by_employee_id === parseInt(currentUserId))
    });
    
    if (userRole === 'Admin') {
      return true; // Admin can modify any template
    }
    if (userRole === 'Change Manager') {
      // Change Manager can only modify templates they created
      // Ensure both sides are integers for comparison
      return template.created_by_employee_id === parseInt(currentUserId);
    }
    return false; // Team Members cannot modify templates
  };

  // Helper function to check if template is system-created (by Admin)
  const isSystemTemplate = (template) => {
    return !template.created_by_employee_id || template.created_by_name === 'System';
  };

  const handleCreateNew = () => {
    setEditingTemplate({ name: '', description: '' });
    setIsCreating(true);
  };

  const handleOpenAIBuilder = () => {
    navigate('/ai-assessment-builder');
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setIsCreating(false);
  };

  const handleDuplicate = async (template) => {
    try {
      await apiClient.post(`/assessment-templates/${template.id}/duplicate`);
      setSuccessMessage('Template duplicated successfully');
      fetchTemplates();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Failed to duplicate template:", err);
      
      // Handle specific error cases
      if (err.response?.status === 403) {
        setError("You do not have permission to duplicate assessment templates. Only administrators and change managers can duplicate templates.");
      } else if (err.response?.status === 404) {
        setError("Template not found. It may have been deleted.");
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Failed to duplicate template. Please try again.");
      }
    }
  };

  const handleDelete = (template) => {
    setDeleteTemplateModal({
      isOpen: true,
      templateId: template.id,
      templateName: template.name
    });
  };

  const confirmDeleteTemplate = async () => {
    try {
      await apiClient.delete(`/assessment-templates/${deleteTemplateModal.templateId}`);
      setSuccessMessage('Template deleted successfully');
      fetchTemplates();
      setDeleteTemplateModal({ isOpen: false, templateId: null, templateName: '' });
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Failed to delete template:", err);
      setDeleteTemplateModal({ isOpen: false, templateId: null, templateName: '' });
      
      // Handle specific error cases
      if (err.response?.status === 403) {
        setError("You do not have permission to delete assessment templates. Only administrators can delete templates.");
      } else if (err.response?.status === 404) {
        setError("Template not found. It may have already been deleted.");
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Failed to delete template. Please try again.");
      }
    }
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      const payload = { ...templateData, title: templateData.name };
      if (isCreating) {
        await apiClient.post('/assessment-templates', payload);
        setSuccessMessage('Template created successfully');
      } else {
        await apiClient.put(`/assessment-templates/${editingTemplate.id}`, payload);
        setSuccessMessage('Template updated successfully');
      }
      setEditingTemplate(null);
      setIsCreating(false);
      fetchTemplates();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Failed to save template:", err);
      
      // Handle specific error cases
      if (err.response?.status === 403) {
        const action = isCreating ? 'create' : 'update';
        const permissions = isCreating ? 'administrators and change managers' : 'administrators';
        setError(`You do not have permission to ${action} assessment templates. Only ${permissions} can ${action} templates.`);
      } else if (err.response?.status === 404 && !isCreating) {
        setError("Template not found. It may have been deleted.");
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Failed to save template. Please try again.");
      }
    }
  };

  const handlePreview = async (template) => {
    setPreviewTemplate(template);
    setIsLoadingPreview(true);
    try {
      const response = await apiClient.get(`/assessment-templates/${template.id}/questions`);
      setPreviewQuestions(response.data);
    } catch (err) {
      setPreviewQuestions([]);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const closePreview = () => {
    setPreviewTemplate(null);
    setPreviewQuestions([]);
  };

  return (
    <div className="template-manager">
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      <div className="template-header">
        <h2>Assessment Templates</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={handleCreateNew} className="create-template-btn">
            <FaPlusCircle style={{ marginRight: 8 }} />
            Create New Template
          </button>
          <button
            onClick={handleOpenAIBuilder}
            className="create-template-btn"
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              color: 'white'
            }}
            title="Create AI-Generated Assessment Template"
          >
            <FaMagic style={{ marginRight: 8 }} />
            AI Builder
          </button>
        </div>
      </div>

      {/* Feature Guide */}
      <div className="feature-guide" style={{ 
        background: '#e3f2fd', 
        border: '1px solid #2196f3', 
        borderRadius: '8px', 
        padding: '1rem', 
        marginBottom: '1.5rem',
        fontSize: '0.9rem',
        color: '#1565c0'
      }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: '#1565c0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FaPlusCircle /> Template Management Features
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
          <div><strong>👁️ Preview:</strong> View how the assessment looks</div>
          <div><strong>✏️ Edit:</strong> Modify your own templates</div>
          <div><strong>📋 Duplicate:</strong> Copy any template with questions</div>
          <div><strong>🗑️ Delete:</strong> Remove your own templates</div>
          <div><strong>➕ Add Questions:</strong> Manage template questions</div>
        </div>
        <div style={{ marginTop: '0.5rem', fontWeight: 'bold', color: '#0d47a1' }}>
          💡 Click the <strong>➕ (Add Question)</strong> button to add, edit, or remove questions from any template.
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#1565c0', background: 'rgba(255,255,255,0.3)', padding: '0.5rem', borderRadius: '4px' }}>
          <strong>📋 Permission Rules:</strong><br/>
          • <strong>System Templates</strong> (created by Admin): All users can view and duplicate, only Admin can edit/delete<br/>
          • <strong>Your Templates</strong>: You have full control (edit, delete, manage questions)<br/>
          • <strong>Other Users' Templates</strong>: You can view and duplicate only
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="empty-state">
          <p>No assessment templates found. Create one to get started.</p>
        </div>
      ) : (
        <>
          {/* Desktop/Tablet Table View */}
          <div style={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            overflow: 'hidden',
            background: 'white'
          }}>
            <table className="template-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ 
                display: 'block',
                background: '#2B9CDB'
              }}>
                <tr>
                  <th style={{ width: '180px', minWidth: '180px' }}>Name</th>
                  <th style={{ width: '250px', minWidth: '250px' }}>Description</th>
                  <th style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>Questions</th>
                  <th style={{ width: '120px', minWidth: '120px' }}>Created By</th>
                  <th style={{ width: '110px', minWidth: '110px' }}>Last Updated</th>
                  <th style={{ width: '280px', minWidth: '280px' }}>Actions</th>
                </tr>
              </thead>
              <tbody style={{ 
                display: 'block',
                height: '400px',
                overflow: 'auto'
              }}>
                {templates.map(template => (
                  <tr key={template.id}>
                      <td style={{ 
                        width: '180px', 
                        minWidth: '180px',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box'
                      }}>
                      {template.name}
                      {isSystemTemplate(template) && (
                        <span style={{ 
                          marginLeft: '0.5rem', 
                          background: '#e3f2fd', 
                          color: '#1565c0', 
                          padding: '2px 6px', 
                          borderRadius: '12px', 
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}>
                          SYSTEM
                        </span>
                      )}
                    </td>
                      <td 
                        style={{ 
                          width: '250px',
                          minWidth: '250px',
                          maxHeight: '60px',
                          overflow: 'hidden',
                          lineHeight: '1.4',
                          wordWrap: 'break-word',
                          whiteSpace: 'normal',
                          position: 'relative',
                          cursor: 'pointer',
                          boxSizing: 'border-box'
                        }}
                        title={template.description || 'No description'}
                      >
                        {template.description || 'No description'}
                      </td>
                      <td style={{ 
                        width: '80px',
                        minWidth: '80px',
                        textAlign: 'center',
                        boxSizing: 'border-box'
                      }}>{template.question_count}</td>
                      <td style={{ 
                        width: '120px',
                        minWidth: '120px',
                        fontSize: '0.9rem', 
                        color: isSystemTemplate(template) ? '#1565c0' : '#666',
                        boxSizing: 'border-box'
                      }}>
                      {template.created_by_name || 'System'}
                    </td>
                      <td style={{
                        width: '110px',
                        minWidth: '110px',
                        boxSizing: 'border-box'
                      }}>{template.last_updated ? new Date(template.last_updated).toLocaleDateString() : 'N/A'}</td>
                      <td style={{
                        width: '280px',
                        minWidth: '280px',
                        boxSizing: 'border-box'
                      }}>
                      <div className="template-actions-icons">
                        <FaEye className="icon-action" title="Preview" onClick={() => handlePreview(template)} />
                        {canModifyTemplate(template) && <FaEdit className="icon-action" title="Edit" onClick={() => handleEdit(template)} />}
                        <FaCopy className="icon-action" title="Duplicate" onClick={() => handleDuplicate(template)} />
                        {canModifyTemplate(template) && <FaTrash className="icon-action" title="Delete" onClick={() => handleDelete(template)} />}
                        {canModifyTemplate(template) && <FaPlusCircle className="icon-action" title="Add Question" onClick={() => {
                          console.log('Add Question clicked for template:', template);
                          setQuestionModalTemplate(template);
                        }} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="template-cards">
            {templates.map(template => (
              <div key={template.id} className="template-card">
                <div className="template-card-header">
                  <div className="template-card-title">
                    {template.name}
                    {isSystemTemplate(template) && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        background: '#e3f2fd', 
                        color: '#1565c0', 
                        padding: '2px 6px', 
                        borderRadius: '12px', 
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}>
                        SYSTEM
                      </span>
                    )}
                  </div>
                </div>
                <div className="template-card-meta">
                  {template.question_count} questions • Updated {template.last_updated ? new Date(template.last_updated).toLocaleDateString() : 'N/A'}
                  <br/>
                  <span style={{ fontSize: '0.8rem', color: isSystemTemplate(template) ? '#1565c0' : '#666' }}>
                    Created by: {template.created_by_name || 'System'}
                  </span>
                </div>
                <div className="template-card-description">
                  {template.description || 'No description provided'}
                </div>
                <div className="template-card-actions">
                  <FaEye className="icon-action" title="Preview" onClick={() => handlePreview(template)} />
                  {canModifyTemplate(template) && <FaEdit className="icon-action" title="Edit" onClick={() => handleEdit(template)} />}
                  <FaCopy className="icon-action" title="Duplicate" onClick={() => handleDuplicate(template)} />
                  {canModifyTemplate(template) && <FaTrash className="icon-action" title="Delete" onClick={() => handleDelete(template)} />}
                  {canModifyTemplate(template) && <FaPlusCircle className="icon-action" title="Add Question" onClick={() => {
                    console.log('Add Question clicked for template (mobile):', template);
                    setQuestionModalTemplate(template);
                  }} />}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <PreviewAssessmentModal
          isOpen={!!previewTemplate}
          onClose={closePreview}
          template={previewTemplate}
          questions={previewQuestions}
          isLoading={isLoadingPreview}
        />
      )}

      {/* Question Manager Modal */}
      {questionModalTemplate && (
        <QuestionManagerModal
          template={questionModalTemplate}
          isOpen={!!questionModalTemplate}
          onClose={() => setQuestionModalTemplate(null)}
          onQuestionsUpdated={fetchTemplates}
        />
      )}

      {/* Create/Edit Template Modal */}
      {editingTemplate && (
        <CreateEditTemplateModal
          isOpen={true}
          onClose={() => {
            setEditingTemplate(null);
            setIsCreating(false);
          }}
          onSave={handleSaveTemplate}
          template={editingTemplate}
        />
      )}

      {/* Delete Template Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteTemplateModal.isOpen}
        onClose={() => setDeleteTemplateModal({ isOpen: false, templateId: null, templateName: '' })}
        onConfirm={confirmDeleteTemplate}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the template "${deleteTemplateModal.templateName}"?`}
        confirmButtonText="Delete Template"
      />
    </div>
  );
}

export default AssessmentTemplateManager;

